import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Typography, IconButton, Chip, TextField, InputAdornment,
    Grid, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Button, Tabs, Tab, CircularProgress,
    Alert, Tooltip, Avatar, Divider, Stack,
} from '@mui/material';
import {
    Search as SearchIcon, Visibility as ViewIcon, SupportAgent as SupportAgentIcon,
    ContactMail as ContactMailIcon, Refresh as RefreshIcon, Email as EmailIcon,
    Phone as PhoneIcon, Business as BusinessIcon, Send as SendIcon,
    Person as PersonIcon, AdminPanelSettings as AdminIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const STATUS_CONFIG = {
    open:        { label: 'Ouvert',    color: 'error',   bg: '#fef2f2' },
    in_progress: { label: 'En cours',  color: 'warning',  bg: '#fffbeb' },
    resolved:    { label: 'Résolu',    color: 'success',  bg: '#f0fdf4' },
    closed:      { label: 'Fermé',     color: 'default',  bg: '#f9fafb' },
};
const PRIORITY_CONFIG = {
    low:    { label: 'Basse',   color: 'success' },
    medium: { label: 'Moyenne', color: 'info' },
    high:   { label: 'Haute',   color: 'warning' },
    urgent: { label: 'Urgent',  color: 'error' },
};
const STATUS_ORDER = ['open', 'in_progress', 'resolved', 'closed'];

function TabPanel({ children, value, index }) {
    return value === index ? <Box>{children}</Box> : null;
}

function StatusChip({ status, size = 'small' }) {
    const cfg = STATUS_CONFIG[status] || { label: status, color: 'default' };
    return <Chip label={cfg.label} color={cfg.color} size={size} />;
}

// ─── Dialog détail ticket ─────────────────────────────────────────────────────
function TicketDetailDialog({ ticket, onClose, onStatusChange, accessToken, canManage }) {
    const [messages, setMessages] = useState([]);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [newMsg, setNewMsg] = useState('');
    const [sendingMsg, setSendingMsg] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(ticket?.status || 'open');
    const [error, setError] = useState(null);
    const bottomRef = useRef(null);

    const headers = { Authorization: `Bearer ${accessToken}` };
    const isSupport = ticket?.service === 'support_technique';

    // Charger les messages si ticket support
    useEffect(() => {
        if (!ticket || !isSupport) return;
        setLoadingMsgs(true);
        axios.get(`${API_URL}/api/tickets/${ticket.id}/messages`, { headers })
            .then(r => setMessages(r.data || []))
            .catch(() => setMessages([]))
            .finally(() => setLoadingMsgs(false));
    }, [ticket?.id]);

    // Scroll auto vers le bas
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleStatusChange = async (newStatus) => {
        setStatusSaving(true);
        setError(null);
        try {
            await axios.patch(`${API_URL}/api/tickets/${ticket.id}/status`,
                { status: newStatus }, { headers });
            setCurrentStatus(newStatus);
            onStatusChange?.(ticket.id, newStatus);
        } catch (e) {
            setError(e.response?.data?.error || 'Erreur lors de la mise à jour du statut');
        } finally {
            setStatusSaving(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMsg.trim()) return;
        setSendingMsg(true);
        setError(null);
        try {
            const r = await axios.post(`${API_URL}/api/tickets/${ticket.id}/messages`,
                { content: newMsg.trim() }, { headers });
            setMessages(prev => [...prev, r.data]);
            setNewMsg('');
        } catch (e) {
            setError(e.response?.data?.error || 'Erreur lors de l\'envoi');
        } finally {
            setSendingMsg(false);
        }
    };

    if (!ticket) return null;

    const cfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.open;

    return (
        <Dialog open={!!ticket} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}>
            {/* Header */}
            <Box sx={{ bgcolor: cfg.bg, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                #{ticket.id?.substring(0, 8).toUpperCase()}
                            </Typography>
                            <StatusChip status={currentStatus} />
                            {ticket.priority && (
                                <Chip
                                    label={PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority}
                                    color={PRIORITY_CONFIG[ticket.priority]?.color || 'default'}
                                    size="small" variant="outlined"
                                />
                            )}
                        </Box>
                        <Typography variant="h6" fontWeight="bold">{ticket.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {new Date(ticket.created_at).toLocaleString('fr-FR')}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%', overflow: { xs: 'auto', md: 'hidden' } }}>

                    {/* ── Colonne gauche : infos + statut ── */}
                    <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, maxHeight: { xs: '40vh', md: 'none' }, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', p: 2, overflowY: 'auto' }}>

                        {/* Changer statut */}
                        <Typography variant="overline" color="text.secondary">Statut</Typography>
                        <Stack spacing={0.5} sx={{ mb: 2 }}>
                            {STATUS_ORDER.map(s => {
                                const c = STATUS_CONFIG[s];
                                const active = currentStatus === s;
                                return (
                                    <Button
                                        key={s}
                                        size="small"
                                        variant={active ? 'contained' : 'outlined'}
                                        color={c.color === 'default' ? 'inherit' : c.color}
                                        disabled={statusSaving || !canManage}
                                        onClick={() => !active && handleStatusChange(s)}
                                        sx={{ justifyContent: 'flex-start', textTransform: 'none',
                                            ...(active ? {} : { opacity: 0.6 }) }}
                                    >
                                        {statusSaving && active
                                            ? <CircularProgress size={14} sx={{ mr: 1 }} />
                                            : null}
                                        {c.label}
                                    </Button>
                                );
                            })}
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Infos contact / créateur */}
                        {ticket.contact_name ? (
                            <>
                                <Typography variant="overline" color="text.secondary">Contact</Typography>
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                                            {ticket.contact_name?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight={600}>{ticket.contact_name}</Typography>
                                    </Box>
                                    {ticket.contact_email && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                            <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                {ticket.contact_email}
                                            </Typography>
                                        </Box>
                                    )}
                                    {ticket.contact_phone && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                            <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">{ticket.contact_phone}</Typography>
                                        </Box>
                                    )}
                                    {ticket.contact_company && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                            <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">{ticket.contact_company}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        ) : ticket.created_by_firstname ? (
                            <>
                                <Typography variant="overline" color="text.secondary">Demandeur</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                                        {ticket.created_by_firstname?.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography variant="body2" fontWeight={600}>
                                        {ticket.created_by_firstname} {ticket.created_by_lastname}
                                    </Typography>
                                </Box>
                            </>
                        ) : null}

                        {/* Source */}
                        <Typography variant="overline" color="text.secondary">Source</Typography>
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                label={ticket.source === 'contact_form' ? 'Formulaire de contact' : 'API'}
                                size="small" variant="outlined"
                            />
                        </Box>

                        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                    </Box>

                    {/* ── Colonne droite : conversation / description ── */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                        {isSupport ? (
                            <>
                                {/* Description initiale */}
                                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                                    <Typography variant="overline" color="text.secondary">Description initiale</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#fafafa' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {ticket.description}
                                        </Typography>
                                    </Paper>
                                </Box>

                                <Divider />

                                {/* Thread messages */}
                                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Historique de conversation ({messages.length} message{messages.length !== 1 ? 's' : ''})
                                    </Typography>

                                    {loadingMsgs ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                                        </Box>
                                    ) : messages.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                            Aucun message pour l'instant
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {messages.map(msg => {
                                                const isAdmin = msg.author_type === 'admin';
                                                return (
                                                    <Box key={msg.id} sx={{ display: 'flex', gap: 1.5,
                                                        flexDirection: isAdmin ? 'row-reverse' : 'row' }}>
                                                        <Avatar sx={{
                                                            width: 30, height: 30, fontSize: 13, flexShrink: 0,
                                                            bgcolor: isAdmin ? 'primary.main' : '#6366f1',
                                                        }}>
                                                            {isAdmin
                                                                ? <AdminIcon sx={{ fontSize: 16 }} />
                                                                : (msg.author_firstname?.charAt(0) || <PersonIcon sx={{ fontSize: 16 }} />)
                                                            }
                                                        </Avatar>
                                                        <Box sx={{ maxWidth: '75%' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
                                                                flexDirection: isAdmin ? 'row-reverse' : 'row', mb: 0.3 }}>
                                                                <Typography variant="caption" fontWeight={600}>
                                                                    {isAdmin
                                                                        ? `${msg.author_firstname || ''} ${msg.author_lastname || ''}`.trim() || 'Admin'
                                                                        : `${msg.author_firstname || ''} ${msg.author_lastname || ''}`.trim() || 'Client'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {new Date(msg.created_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                                                                </Typography>
                                                            </Box>
                                                            <Paper variant="outlined" sx={{
                                                                p: 1.5,
                                                                bgcolor: isAdmin ? '#FFF4EE' : '#f5f3ff',
                                                                borderColor: isAdmin ? '#FFD9C4' : '#ddd6fe',
                                                            }}>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                    {msg.content}
                                                                </Typography>
                                                            </Paper>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                            <div ref={bottomRef} />
                                        </Stack>
                                    )}
                                </Box>

                                {/* Zone de saisie */}
                                <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                        <TextField
                                            multiline
                                            minRows={1}
                                            maxRows={4}
                                            fullWidth
                                            size="small"
                                            placeholder="Répondre au ticket..."
                                            value={newMsg}
                                            onChange={e => setNewMsg(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                            disabled={sendingMsg}
                                        />
                                        <IconButton
                                            onClick={handleSendMessage}
                                            disabled={!newMsg.trim() || sendingMsg}
                                            sx={{ bgcolor: 'primary.main', color: 'white',
                                                '&:hover': { bgcolor: '#a20000' },
                                                '&.Mui-disabled': { bgcolor: '#f5f5f5' },
                                                flexShrink: 0 }}
                                        >
                                            {sendingMsg ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                        </IconButton>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Entrée pour envoyer · Maj+Entrée pour sauter une ligne
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            /* Prise de contact : affichage enrichi de la description */
                            <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
                                <Typography variant="overline" color="text.secondary">Message</Typography>
                                <Paper variant="outlined" sx={{ p: 2.5, mt: 1, bgcolor: '#fafafa' }}>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                                        {ticket.description}
                                    </Typography>
                                </Paper>

                                {/* Actions rapides prise de contact */}
                                {ticket.contact_email && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="overline" color="text.secondary">Actions rapides</Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<EmailIcon />}
                                                href={`mailto:${ticket.contact_email}?subject=Re: ${encodeURIComponent(ticket.title)}`}
                                            >
                                                Répondre par email
                                            </Button>
                                            {ticket.contact_phone && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<PhoneIcon />}
                                                    href={`tel:${ticket.contact_phone}`}
                                                >
                                                    Appeler
                                                </Button>
                                            )}
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function TicketsPage() {
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canSupport = isSuperAdmin || userPermissions.includes('tickets.support');
    const canCommercial = isSuperAdmin || userPermissions.includes('tickets.commercial');
    const canManageTickets = isSuperAdmin || userPermissions.includes('tickets.manage');
    const [tab, setTab] = useState(canSupport ? 0 : 1);

    const [supportTickets, setSupportTickets] = useState([]);
    const [supportLoading, setSupportLoading] = useState(false);
    const [supportError, setSupportError] = useState(null);
    const [supportPage, setSupportPage] = useState(0);
    const [supportTotal, setSupportTotal] = useState(0);
    const [supportRowsPerPage, setSupportRowsPerPage] = useState(10);
    const [supportSearch, setSupportSearch] = useState('');
    const [supportStatusFilter, setSupportStatusFilter] = useState('all');
    const [supportPriorityFilter, setSupportPriorityFilter] = useState('all');

    const [contactTickets, setContactTickets] = useState([]);
    const [contactLoading, setContactLoading] = useState(false);
    const [contactError, setContactError] = useState(null);
    const [contactPage, setContactPage] = useState(0);
    const [contactTotal, setContactTotal] = useState(0);
    const [contactRowsPerPage, setContactRowsPerPage] = useState(10);
    const [contactSearch, setContactSearch] = useState('');
    const [contactStatusFilter, setContactStatusFilter] = useState('all');

    const [summary, setSummary] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);

    const authHeaders = { Authorization: `Bearer ${accessToken}` };

    const fetchSummary = useCallback(async () => {
        if (!accessToken) return;
        try {
            const r = await axios.get(`${API_URL}/api/tickets/summary`, { headers: authHeaders });
            setSummary(r.data);
        } catch {}
    }, [accessToken]);

    const fetchSupportTickets = useCallback(async () => {
        if (!accessToken) return;
        setSupportLoading(true); setSupportError(null);
        try {
            const params = { service: 'support_technique', page: supportPage + 1, limit: supportRowsPerPage };
            if (supportStatusFilter !== 'all') params.status = supportStatusFilter;
            if (supportPriorityFilter !== 'all') params.priority = supportPriorityFilter;
            const r = await axios.get(`${API_URL}/api/tickets`, { headers: authHeaders, params });
            setSupportTickets(r.data.data || r.data.tickets || []);
            setSupportTotal(r.data.total || 0);
        } catch (e) {
            setSupportError(e.response?.data?.error || e.message);
        } finally {
            setSupportLoading(false);
        }
    }, [accessToken, supportPage, supportRowsPerPage, supportStatusFilter, supportPriorityFilter]);

    const fetchContactTickets = useCallback(async () => {
        if (!accessToken) return;
        setContactLoading(true); setContactError(null);
        try {
            const params = { service: 'commercial', page: contactPage + 1, limit: contactRowsPerPage };
            if (contactStatusFilter !== 'all') params.status = contactStatusFilter;
            const r = await axios.get(`${API_URL}/api/tickets`, { headers: authHeaders, params });
            setContactTickets(r.data.data || r.data.tickets || []);
            setContactTotal(r.data.total || 0);
        } catch (e) {
            setContactError(e.response?.data?.error || e.message);
        } finally {
            setContactLoading(false);
        }
    }, [accessToken, contactPage, contactRowsPerPage, contactStatusFilter]);

    useEffect(() => { fetchSummary(); }, [fetchSummary]);
    useEffect(() => { fetchSupportTickets(); }, [fetchSupportTickets]);
    useEffect(() => { fetchContactTickets(); }, [fetchContactTickets]);

    const supportStats = summary?.find(s => s.service === 'support_technique') || {};
    const commercialStats = summary?.find(s => s.service === 'commercial') || {};

    const filteredSupport = supportTickets.filter(t =>
        !supportSearch ||
        t.title?.toLowerCase().includes(supportSearch.toLowerCase()) ||
        t.description?.toLowerCase().includes(supportSearch.toLowerCase())
    );
    const filteredContact = contactTickets.filter(t =>
        !contactSearch ||
        t.title?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        t.contact_name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        t.contact_email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        t.contact_company?.toLowerCase().includes(contactSearch.toLowerCase())
    );

    // Mise à jour du statut dans la liste après modification dans le dialog
    const handleStatusChange = (ticketId, newStatus) => {
        setSupportTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
        setContactTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
        if (selectedTicket?.id === ticketId) {
            setSelectedTicket(prev => ({ ...prev, status: newStatus }));
        }
    };

    if (!canSupport && !canCommercial) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">Accès refusé</Typography>
                <Typography color="text.secondary">Vous n'avez accès ni aux tickets support ni aux tickets commerciaux.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Support & Tickets
            </Typography>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {[
                    { label: 'Support technique', value: supportStats.total || supportTickets.length, sub: `${(supportStats.open || 0) + (supportStats.in_progress || 0)} en attente` },
                    { label: 'Prises de contact', value: commercialStats.total || contactTickets.length, sub: `${(commercialStats.open || 0) + (commercialStats.in_progress || 0)} en attente` },
                    { label: 'Ouverts', value: (supportStats.open || 0) + (commercialStats.open || 0), sub: 'à traiter' },
                    { label: 'Résolus', value: (supportStats.resolved || 0) + (commercialStats.resolved || 0), sub: 'ce mois' },
                ].map(s => (
                    <Grid item xs={12} sm={6} md={3} key={s.label}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom variant="body2">{s.label}</Typography>
                                <Typography variant="h4" sx={{ color: 'primary.main' }}>{s.value}</Typography>
                                <Typography variant="caption" color="text.secondary">{s.sub}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Paper variant="outlined">
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                    TabIndicatorProps={{ sx: { bgcolor: 'primary.main' } }}>
                    {canSupport && (
                        <Tab value={0} icon={<SupportAgentIcon />} iconPosition="start" label="Support technique"
                            sx={{ '&.Mui-selected': { color: 'primary.main' } }} />
                    )}
                    {canCommercial && (
                        <Tab value={1} icon={<ContactMailIcon />} iconPosition="start" label="Prises de contact"
                            sx={{ '&.Mui-selected': { color: 'primary.main' } }} />
                    )}
                </Tabs>

                {/* ─── Tab Support Technique ─── */}
                <TabPanel value={tab} index={0}>
                    <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Rechercher..."
                            value={supportSearch}
                            onChange={e => setSupportSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                            sx={{ flexGrow: 1, minWidth: 220 }} size="small"
                        />
                        <FormControl sx={{ minWidth: 120 }} size="small">
                            <InputLabel>Statut</InputLabel>
                            <Select value={supportStatusFilter}
                                onChange={e => { setSupportStatusFilter(e.target.value); setSupportPage(0); }} label="Statut">
                                <MenuItem value="all">Tous</MenuItem>
                                {Object.entries(STATUS_CONFIG).map(([k, v]) =>
                                    <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 120 }} size="small">
                            <InputLabel>Priorité</InputLabel>
                            <Select value={supportPriorityFilter}
                                onChange={e => { setSupportPriorityFilter(e.target.value); setSupportPage(0); }} label="Priorité">
                                <MenuItem value="all">Toutes</MenuItem>
                                {Object.entries(PRIORITY_CONFIG).map(([k, v]) =>
                                    <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Tooltip title="Rafraîchir">
                            <IconButton onClick={fetchSupportTickets} size="small"><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Box>

                    {supportError && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{supportError}</Alert>}

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 700 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ticket</TableCell>
                                    <TableCell>Priorité</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>Créé par</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {supportLoading ? (
                                    <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ color: 'primary.main', my: 2 }} /></TableCell></TableRow>
                                ) : filteredSupport.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>Aucun ticket</TableCell></TableRow>
                                ) : filteredSupport.map(ticket => (
                                    <TableRow key={ticket.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold">{ticket.title}</Typography>
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                                                {ticket.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {ticket.priority
                                                ? <Chip label={PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority}
                                                    color={PRIORITY_CONFIG[ticket.priority]?.color || 'default'} size="small" />
                                                : '—'}
                                        </TableCell>
                                        <TableCell><StatusChip status={ticket.status} /></TableCell>
                                        <TableCell>
                                            {ticket.created_by_firstname
                                                ? `${ticket.created_by_firstname} ${ticket.created_by_lastname || ''}`.trim()
                                                : '—'}
                                        </TableCell>
                                        <TableCell>{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Voir le détail">
                                                <IconButton size="small" onClick={() => setSelectedTicket(ticket)}
                                                    sx={{ color: 'primary.main' }}>
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]} component="div"
                        count={supportTotal || filteredSupport.length}
                        rowsPerPage={supportRowsPerPage} page={supportPage}
                        onPageChange={(_, p) => setSupportPage(p)}
                        onRowsPerPageChange={e => { setSupportRowsPerPage(parseInt(e.target.value, 10)); setSupportPage(0); }}
                        labelRowsPerPage="Lignes :"
                    />
                </TabPanel>

                {/* ─── Tab Prises de contact ─── */}
                <TabPanel value={tab} index={1}>
                    <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Rechercher (nom, email, entreprise)..."
                            value={contactSearch}
                            onChange={e => setContactSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                            sx={{ flexGrow: 1, minWidth: 250 }} size="small"
                        />
                        <FormControl sx={{ minWidth: 120 }} size="small">
                            <InputLabel>Statut</InputLabel>
                            <Select value={contactStatusFilter}
                                onChange={e => { setContactStatusFilter(e.target.value); setContactPage(0); }} label="Statut">
                                <MenuItem value="all">Tous</MenuItem>
                                {Object.entries(STATUS_CONFIG).map(([k, v]) =>
                                    <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Tooltip title="Rafraîchir">
                            <IconButton onClick={fetchContactTickets} size="small"><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Box>

                    {contactError && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{contactError}</Alert>}

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 750 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sujet</TableCell>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Entreprise</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contactLoading ? (
                                    <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} sx={{ color: 'primary.main', my: 2 }} /></TableCell></TableRow>
                                ) : filteredContact.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>Aucune prise de contact</TableCell></TableRow>
                                ) : filteredContact.map(ticket => (
                                    <TableRow key={ticket.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold">{ticket.title}</Typography>
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                                                {ticket.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">{ticket.contact_name || '—'}</Typography>
                                            {ticket.contact_email && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary">{ticket.contact_email}</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {ticket.contact_company
                                                ? <Typography variant="body2">{ticket.contact_company}</Typography>
                                                : '—'}
                                        </TableCell>
                                        <TableCell><StatusChip status={ticket.status} /></TableCell>
                                        <TableCell>
                                            <Chip
                                                label={ticket.source === 'contact_form' ? 'Formulaire' : 'API'}
                                                variant="outlined" size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                        <TableCell>
                                            <Tooltip title="Voir le détail">
                                                <IconButton size="small" onClick={() => setSelectedTicket(ticket)}
                                                    sx={{ color: 'primary.main' }}>
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]} component="div"
                        count={contactTotal || filteredContact.length}
                        rowsPerPage={contactRowsPerPage} page={contactPage}
                        onPageChange={(_, p) => setContactPage(p)}
                        onRowsPerPageChange={e => { setContactRowsPerPage(parseInt(e.target.value, 10)); setContactPage(0); }}
                        labelRowsPerPage="Lignes :"
                    />
                </TabPanel>
            </Paper>

            <TicketDetailDialog
                ticket={selectedTicket}
                onClose={() => setSelectedTicket(null)}
                onStatusChange={handleStatusChange}
                accessToken={accessToken}
                canManage={canManageTickets}
            />
        </Box>
    );
}
